import { Link } from "react-router-dom";
import { Swords, Users, Trophy, Shield, Heart } from "lucide-react";

export default function About() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="font-cinzel text-4xl md:text-5xl text-amber-300 uppercase tracking-wide mb-2">
          About Lanista Manager
        </h1>
        <p className="text-zinc-400 text-lg">The free online gladiator management game</p>
      </div>

      <div className="space-y-8 text-zinc-300">
        {/* Introduction */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Swords size={24} /> What is Lanista Manager?
          </h2>
          <p className="leading-relaxed mb-4">
            Lanista Manager is a free-to-play online gladiator management game where you step into the sandals of a Roman lanista — a trainer and owner of gladiators. Build your ludus (gladiator school), recruit fighters, train them in real-time, forge legendary equipment, and compete against other players in tactical arena combat.
          </p>
          <p className="leading-relaxed">
            Set in ancient Rome, Lanista Manager combines strategic roster management, real-time training progression, equipment crafting, beast taming, and PvP battles into an immersive browser-based experience. Whether you're a fan of ancient history, strategy games, or competitive gaming, Lanista offers deep gameplay that rewards both tactical thinking and long-term planning.
          </p>
        </section>

        {/* Core Features */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Trophy size={24} /> Core Features
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Gladiator Management</h3>
              <p className="text-sm leading-relaxed">
                Recruit gladiators from six distinct classes (Murmillo, Retiarius, Thraex, Hoplomachus, Secutor, Dimachaerus), each with unique fighting styles and equipment. Train their skills, manage their age and retirement, and watch them grow from rookies to arena legends.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Real-Time Training</h3>
              <p className="text-sm leading-relaxed">
                Training occurs in real-time with sessions at 00:00, 08:00, and 16:00 daily. Choose which attributes to focus on (Strength, Speed, Stamina, Defense, Precision, Technique, Aggression) and watch your gladiators improve over time. Hidden potential affects growth rates.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Equipment Forging</h3>
              <p className="text-sm leading-relaxed">
                Collect weapon and armor fragments from victories, then forge equipment in five rarity tiers (Common to Legendary). Equip your gladiators with helmets, armor, shields, and weapons. Items degrade with use and require repairs.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Beast System (Vivarium)</h3>
              <p className="text-sm leading-relaxed">
                Build a vivarium to capture and breed exotic beasts (bears, wolves, leopards, tigers). Equip beasts to gladiators for combat bonuses. Complete breeding quests for rare offspring with enhanced stats. Upgrade your vivarium to house more creatures.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">PvP Combat & Rankings</h3>
              <p className="text-sm leading-relaxed">
                Challenge other players in ranked arena battles. Climb the leaderboards through Bronze, Silver, Gold, Platinum, and Diamond tiers. Each fight plays out with tactical turn-by-turn commentary. Build win streaks and earn trophies.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Alliances & Social</h3>
              <p className="text-sm leading-relaxed">
                Join or create alliances with other lanistas. Contribute to alliance treasuries, compete in alliance rankings, chat with members, and participate in co-op community events like world boss battles.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Imperial Campaign</h3>
              <p className="text-sm leading-relaxed">
                Progress through provinces (Italia, Thracia, Aegyptus, Britannia) in a PvE campaign. Choose your path through branching nodes, face increasingly difficult AI opponents, and earn unique rewards.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">Tournaments & Events</h3>
              <p className="text-sm leading-relaxed">
                Compete in weekly tournaments with bracket-based elimination. Participate in limited-time events like community world boss fights. Win exclusive rewards and earn your place in Lanista history.
              </p>
            </div>
          </div>
        </section>

        {/* Game Philosophy */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Heart size={24} /> Our Philosophy
          </h2>
          <p className="leading-relaxed mb-4">
            <strong className="text-amber-400">Free-to-play, not pay-to-win.</strong> Lanista Manager is completely free to play with optional VIP membership that provides convenience (more roster slots, faster training, custom avatars) but does not guarantee victories. Skill, strategy, and dedication determine success in the arena.
          </p>
          <p className="leading-relaxed mb-4">
            <strong className="text-amber-400">Respect your time.</strong> Real-time training means you don't need to be online 24/7. Set your gladiators to train, check back at session times, and make strategic decisions. The game rewards consistent engagement, not constant grinding.
          </p>
          <p className="leading-relaxed">
            <strong className="text-amber-400">Community-driven.</strong> We listen to player feedback and actively iterate on features. Join our community, share your suggestions, and help shape the future of Lanista Manager.
          </p>
        </section>

        {/* Getting Started */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Users size={24} /> Getting Started
          </h2>
          <p className="leading-relaxed mb-4">
            Ready to begin your journey as a lanista? Creating an account is free and takes seconds. Once registered, you'll receive starting gold to recruit your first gladiators and begin training immediately.
          </p>
          <p className="leading-relaxed mb-4">
            New players start with a guided tutorial that teaches the basics of roster management, training, equipment, and combat. The community is welcoming — don't hesitate to ask questions in the arena chat or seek advice from veteran lanistas.
          </p>
          <div className="flex gap-4 mt-6">
            <Link to="/login" className="bg-amber-600 hover:bg-amber-700 text-zinc-950 font-bold px-6 py-3 rounded transition-colors">
              Create Free Account
            </Link>
            <Link to="/" className="border border-amber-600 text-amber-400 hover:bg-amber-950/30 font-semibold px-6 py-3 rounded transition-colors">
              Learn More
            </Link>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Shield size={24} /> Contact & Support
          </h2>
          <p className="leading-relaxed mb-4">
            Have questions, feedback, or need help? We're here for you.
          </p>
          <ul className="space-y-2 text-sm">
            <li>• <Link to="/contact" className="text-amber-400 hover:underline">Contact Form</Link> — Send us a message</li>
            <li>• <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link> — Rules and guidelines</li>
            <li>• <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link> — How we protect your data</li>
            <li>• In-game Arena Chat — Talk to the community and moderators</li>
          </ul>
        </section>

        {/* Footer note */}
        <div className="text-center pt-6 space-y-4">
          <div className="flex justify-center gap-4 text-sm">
            <Link to="/faq" className="text-amber-400 hover:underline">FAQ</Link>
            <Link to="/guide" className="text-amber-400 hover:underline">How to Play</Link>
            <Link to="/hall" className="text-amber-400 hover:underline">Hall of Fame</Link>
            <Link to="/" className="text-amber-400 hover:underline">Home</Link>
          </div>
          <p className="text-sm text-zinc-500">Lanista Manager © 2024-2026. All gladiatorial combat is simulated. No actual gladiators were harmed in the making of this game.</p>
        </div>
      </div>
    </div>
  );
}
